import apiClient, { handleApiResponse, handleApiError } from './apiClient';

/**
 * CV Processing Service
 * Handles CV upload, processing, and AI analysis through backend
 */
class CvProcessingService {
  /**
   * Upload CV for processing
   * @param {object} cvData - CV upload data
   * @returns {Promise<object>} Upload response with extracted info
   */
  async uploadCv(cvData) {
    try {
      const response = await apiClient.post('/cv-processing/upload', {
        cv_text: cvData.cvText,
        candidate_name: cvData.candidateName,
        job_position: cvData.jobPosition,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Process CV with comprehensive analysis
   * @param {string} cvId - CV UUID
   * @param {object} processingOptions - Processing parameters
   * @returns {Promise<object>} Processing response with analysis
   */
  async processCv(cvId, processingOptions) {
    try {
      const response = await apiClient.post(`/cv-processing/${cvId}/process`, {
        job_position: processingOptions.jobPosition,
        job_requirements: processingOptions.jobRequirements,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Get CV processing status
   * @param {string} cvId - CV UUID
   * @returns {Promise<object>} Processing status
   */
  async getProcessingStatus(cvId) {
    try {
      const response = await apiClient.get(`/cv-processing/${cvId}/status`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Analyze CV using OpenAI (direct backend call)
   * @param {object} analysisData - CV analysis parameters
   * @returns {Promise<object>} AI analysis results
   */
  async analyzeCv(analysisData) {
    try {
      const response = await apiClient.post('/openai/analyze-cv', {
        cv_text: analysisData.cvText,
        job_position: analysisData.jobPosition,
        job_requirements: analysisData.jobRequirements,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Extract key information from CV
   * @param {string} cvText - CV text content
   * @returns {Promise<object>} Extracted CV information
   */
  async extractCvInfo(cvText) {
    try {
      const response = await apiClient.post('/openai/extract-cv-info', {
        cv_text: cvText,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Process multiple CVs in batch
   * @param {Array} cvDataArray - Array of CV data objects
   * @returns {Promise<Array>} Array of processing results
   */
  async batchProcessCvs(cvDataArray) {
    try {
      // Use the backend bulk processing endpoint
      const response = await apiClient.post('/cv-processing/bulk-process', 
        cvDataArray.map(cvData => ({
          cv_text: cvData.cvText,
          candidate_name: cvData.candidateName,
          job_position: cvData.jobPosition,
        }))
      );
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Get CV processing history
   * @param {object} options - Query options
   * @returns {Promise<object>} Processing history
   */
  async getProcessingHistory(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.pageSize) params.append('page_size', options.pageSize);
      if (options.status) params.append('status', options.status);
      if (options.dateFrom) params.append('date_from', options.dateFrom);
      if (options.dateTo) params.append('date_to', options.dateTo);
      
      // This would be implemented when history endpoints are available
      const response = await apiClient.get(`/cv-processing/history?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Submit job application with CV
   * @param {object} applicationData - Job application data
   * @returns {Promise<object>} Application submission result
   */
  async submitJobApplication(applicationData) {
    try {
      const response = await apiClient.post('/job-applications/submit', {
        job_id: applicationData.jobId,
        candidate_name: applicationData.candidateName,
        candidate_email: applicationData.candidateEmail,
        candidate_phone: applicationData.candidatePhone,
        cv_text: applicationData.cvText,
        cover_letter: applicationData.coverLetter,
        auto_process_cv: applicationData.autoProcessCv,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Get job applications for a specific job
   * @param {string} jobId - Job ID
   * @param {object} options - Query options
   * @returns {Promise<object>} Job applications
   */
  async getJobApplications(jobId, options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.pageSize) params.append('page_size', options.pageSize);
      if (options.status) params.append('status', options.status);
      
      const response = await apiClient.get(`/jobs/${jobId}/applications?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }
}

export default new CvProcessingService();